import { z } from 'zod';

import { ToneName, Ulid } from './contracts/primitives';

/** What a media URL points at (docs/02 §3). */
export type MediaKind = 'mp4' | 'tone' | 'hls' | 'image';

/** Signed query on `media.acapella.lol/m/*` URLs: HMAC(secret, songId|sid|exp). */
export const SignedMediaQuery = z.object({
  exp: z.coerce.number().int().positive(),
  sid: Ulid,
  sig: z.string().regex(/^[0-9a-f]{64}$/, 'Expected a hex HMAC-SHA256'),
});
export type SignedMediaQuery = z.infer<typeof SignedMediaQuery>;

/** Inputs to `signMediaUrl(songId, path, sid, ttl)` in the API (docs/02 §3). */
export interface SignMediaParams {
  songId: string;
  /** Path under `/m/`, e.g. `<songId>.mp4`, `<songId>/tones/+1.m4a`, `<songId>/hls/master.m3u8`. */
  path: string;
  sid: string;
  /** Seconds, default {@link MEDIA_URL_TTL_SECONDS}. */
  ttl?: number;
}

/** Default signed-URL lifetime in seconds (docs/01 §7: 10 minutes). */
export const MEDIA_URL_TTL_SECONDS = 600;

/** Image transform query for `/img/{key}` (Cloudflare Images transform). */
export const ImageTransform = z.object({
  w: z.coerce.number().int().min(1).max(4096).optional(),
  h: z.coerce.number().int().min(1).max(4096).optional(),
  f: z.enum(['auto', 'webp', 'avif', 'jpeg', 'png']).optional(),
});
export type ImageTransform = z.infer<typeof ImageTransform>;

/** Path builders for the media Worker (all relative to `mediaBase`). */
export const mediaPaths = {
  mp4: (songId: string) => `/m/${songId}.mp4`,
  tone: (songId: string, tone: ToneName) => `/m/${songId}/tones/${encodeURIComponent(tone)}.m4a`,
  hls: (songId: string, file = 'master.m3u8') => `/m/${songId}/hls/${file}`,
  /** Synced lyrics (`songs.lyrics_lrc_path`), signed like every `/m/*` path. */
  lrc: (songId: string) => `/m/${songId}/lyrics.lrc`,
  img: (key: string) => `/img/${key.split('/').map(encodeURIComponent).join('/')}`,
} as const;

/** Full image URL with optional transform, e.g. for cards at exact display sizes. */
export function imageUrl(mediaBase: string, key: string, transform?: ImageTransform): string {
  const base = mediaBase.replace(/\/+$/, '');
  const qs = new URLSearchParams();
  if (transform?.w) qs.set('w', String(transform.w));
  if (transform?.h) qs.set('h', String(transform.h));
  if (transform?.f) qs.set('f', transform.f);
  const q = qs.toString();
  return `${base}${mediaPaths.img(key)}${q ? `?${q}` : ''}`;
}

/** Parses the tone name out of a derived tone path; `null` when not a tone path. */
export function toneFromMediaPath(path: string): ToneName | null {
  const m = /\/tones\/([^/]+)\.m4a$/.exec(path);
  if (!m) return null;
  const parsed = ToneName.safeParse(decodeURIComponent(m[1] ?? ''));
  return parsed.success ? parsed.data : null;
}

/**
 * The only widths the media Worker keeps thumbnails for (`/img/{key}?w=`). A request for another
 * width is rounded UP to the next one; wider than the last gets the original. Keeping the set
 * fixed is what bounds the number of objects the Worker can ever store per picture.
 */
export const IMAGE_WIDTHS = [96, 160, 240, 320, 480, 640] as const;

/**
 * `url` asked at the thumbnail width that covers `cssWidth` css pixels on a screen of density
 * `dpr` (1 on the TV stage, 2 for a phone). Only media-Worker image URLs are touched; anything
 * else, and anything that needs more than the widest thumbnail, is returned as it came.
 */
export function sizedImageUrl(
  url: string | null | undefined,
  cssWidth: number,
  dpr = 1,
): string | null | undefined {
  if (!url || !url.includes('/img/') || !(cssWidth > 0)) return url;
  const needed = Math.ceil(cssWidth * dpr);
  const width = IMAGE_WIDTHS.find((w) => w >= needed);
  if (!width) return url;
  const at = url.indexOf('?');
  const base = at < 0 ? url : url.slice(0, at);
  const query = new URLSearchParams(at < 0 ? '' : url.slice(at + 1));
  query.set('w', String(width));
  return `${base}?${query.toString()}`;
}
